import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as GatsbyLink } from 'gatsby';
import {
  Alert, Link, Heading, Pagination, Spinner, Empty, scrollIntoView,
} from '@printdeal/ui-library';
import Breadcrumb from '../../../../Breadcrumb';
import ApiContext from '../../contexts/ApiContext';
import { messages } from '../../../translations/savedDesigns';
import Head from '../../../../PrivateHead';
import Design, { STATES } from './Design';
import { Design as DesignType } from '../../../../../types/design';

const PAGE_SIZE = 20;

const DesignListPage = () => {
  const intl = useIntl();
  const { designsApi } = useContext(ApiContext);
  const [designs, setDesigns] = useState<DesignType[][]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageHash, setNextPageHash] = useState<number>(0);
  const [status, setStatus] = useState(STATES.INITIAL);
  const currentDesignsList = designs[currentPage - 1];

  if (!designsApi) return null;

  const fetchDesigns = async () => {
    setStatus(STATES.GET_DESIGNS.LOADING);
    try {
      const { designs: fetchedDesigns, nextPage } = await designsApi.getSavedDesigns(PAGE_SIZE, nextPageHash);
      if (fetchedDesigns.length > 0) {
        setDesigns([...designs, fetchedDesigns]);
        setStatus(STATES.GET_DESIGNS.SUCCESS);
      } else {
        setStatus(STATES.GET_DESIGNS.NO_RESULTS);
      }
      setNextPageHash(nextPage || 0);
    } catch (error) {
      setStatus(STATES.GET_DESIGNS.ERROR);
    }
  };

  const updateDesign = (updatedDesign: { id: string, deleted: boolean }) => {
    const { id } = updatedDesign;
    const designIndex = currentDesignsList.findIndex((design) => design.id === id);
    const currentDesigns = [...designs];
    currentDesigns[currentPage - 1][designIndex] = { ...currentDesignsList[designIndex], ...updatedDesign };
    setDesigns(currentDesigns);
  };

  const switchPage = (pageNumber: number) => {
    const title = document.getElementById('design-list-title');
    if (title !== null) {
      scrollIntoView(title, {
        behavior: 'smooth',
      });
    }
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    // Load the first page of designs
    fetchDesigns();
  }, []);

  useEffect(() => {
    // If we're at the last page of the loaded designs, and more are available, load the next page
    if (currentPage === designs.length && nextPageHash) {
      fetchDesigns();
    }
  }, [designs, nextPageHash, currentPage]);

  return (
    <>
      <Head title={intl.formatMessage(messages.seoTitle)} />
      <div className="pd-grid pd-gap-6">
        <Breadcrumb>
          <Breadcrumb.Item to="..">
            <FormattedMessage id="account.index.title" defaultMessage="My account" />
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrentPage>
            <FormattedMessage id="account.designList.seoTitle" defaultMessage="My designs" />
          </Breadcrumb.Item>
        </Breadcrumb>

        <Heading id="design-list-title">
          <FormattedMessage id="account.designList.seoTitle" defaultMessage="My designs" />
        </Heading>

        {status === STATES.GET_DESIGNS.ERROR && (
          <Alert variant="error">
            <FormattedMessage
              id="account.designList.loadingError"
              defaultMessage="We were unable to load your designs."
            />
          </Alert>
        )}

        {status === STATES.DUPLICATE_DESIGN.ERROR && (
          <Alert variant="error">
            <FormattedMessage
              id="account.designList.addToCartError"
              defaultMessage="Something went wrong while adding your design to the cart."
            />
          </Alert>
        )}

        {status === STATES.GET_DESIGNS.LOADING && (
          <div className="pd-text-center">
            <Spinner
              variant="primary"
              size="lg"
              className="pd-my-4"
              screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
            />
          </div>
        )}

        {status === STATES.GET_DESIGNS.NO_RESULTS && (
          <Empty
            title={intl.formatMessage({
              id: 'account.designList.noResults.title',
              defaultMessage: 'No results found',
            })}
            description={intl.formatMessage({
              id: 'account.designList.noResults',
              defaultMessage: "It seems you haven't saved any designs yet.",
            })}
          />
        )}

        {currentDesignsList && (
          <ol className="pd-grid pd-gap-6">
            {currentDesignsList.map((design) => (
              <Design
                key={design.id}
                design={design}
                status={status}
                setStatus={setStatus}
                updateDesign={updateDesign}
              />
            ))}
          </ol>
        )}

        {designs.length > 0 && (
          <Pagination total={designs.length} currentPage={currentPage} aria-label={intl.formatMessage({ id: 'general.pagination.label', defaultMessage: 'Pagination' })} perPage={PAGE_SIZE} siblingPages={3} className="pd-justify-center">
            {({ pages, totalPages }) => (
              <>
                <Pagination.Button as="button" variant="prev" onClick={switchPage}>
                  <FormattedMessage id="general.pagination.button.previous" defaultMessage="Previous" />
                </Pagination.Button>

                <Pagination.Items className="pd-hidden sm:pd-flex">
                  {pages.map((pageNumber) => <Pagination.Item as="button" key={pageNumber} onClick={switchPage} />)}
                </Pagination.Items>

                <Pagination.Label className="sm:pd-hidden">
                  <FormattedMessage
                    id="general.pagination.mobileText"
                    defaultMessage="Page {pageNumber} of {totalPages}"
                    values={{
                      pageNumber: currentPage,
                      totalPages,
                    }}
                  />
                </Pagination.Label>

                <Pagination.Button as="button" variant="next" onClick={switchPage}>
                  <FormattedMessage id="general.pagination.button.Next" defaultMessage="Next" />
                </Pagination.Button>
              </>
            )}
          </Pagination>
        )}

        <Link to=".." as={GatsbyLink} iconBefore="chevron-left" variant="secondary">
          <FormattedMessage id="account.linkToAccount" defaultMessage="Link to account" />
        </Link>
      </div>
    </>
  );
};

export default DesignListPage;
